import React from 'react'
import { LineHeader } from './line-header'
import { LineTextEditor } from './line-text-editor'
import { LineFooter } from './line-footer'
import { PodcastStatusEnum, type PodcastStatusValue } from '../../../../types/enums'

import { ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem, ContextMenuLabel } from '../../../../components/ui/contextmenu'
import { MoreVertical, Trash2, Plus, Check, Loader2 } from 'lucide-react'
import Button from '../../../../components/ui/button'
import { usePodcastEditor } from '../../context/use-podcast-editor'
import { useConfirm } from '../../../../context/confirm-context'
import type { ScriptSuggestionChange } from '../../../../types/script-refinement'
import { LineSuggestionCard } from '../editor/suggestions/line-suggestion-card'

export const PodcastLineItem: React.FC<{
  line: {
    id: string
    host: string
    text: string
    audioFileUrl?: string | null
    status?: PodcastStatusValue
    estimatedCredits?: number
    emotion?: string
  }
  index?: number
  hostLabel?: string
  hostColorClass?: string
  hostLabels?: Record<string, string>
  onTextInput?: (text: string) => void
  onDelete?: () => void
  onAddLineBelow?: () => void
  suggestions?: ScriptSuggestionChange[]
  activeSuggestionId?: string | null
  onAcceptSuggestion?: (suggestion: ScriptSuggestionChange) => void
  onRejectSuggestion?: (suggestion: ScriptSuggestionChange) => void
  onFocusSuggestion?: (suggestion: ScriptSuggestionChange) => void
  applyingSuggestionId?: string | null
  suggestionsDisabled?: boolean
}> = ({
  line,
  index,
  hostLabel,
  hostColorClass,
  hostLabels,
  onTextInput,
  onDelete,
  onAddLineBelow,
  suggestions,
  activeSuggestionId,
  onAcceptSuggestion,
  onRejectSuggestion,
  onFocusSuggestion,
  applyingSuggestionId,
  suggestionsDisabled,
}) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null)
    const [expanded, setExpanded] = React.useState(false)
    const [editText, setEditText] = React.useState(line.text)
    const [isEditing, setIsEditing] = React.useState(false)
    const [pendingDelete, setPendingDelete] = React.useState(false)

    const isProcessing = line.status === PodcastStatusEnum.Processing

    React.useEffect(() => {
      if (!isEditing) {
        setEditText(line.text)
      }
    }, [line.id, line.text, isEditing])

    React.useEffect(() => { setPendingDelete(false) }, [line.id])
    React.useEffect(() => {
      if (!pendingDelete) return
      const t = setTimeout(() => setPendingDelete(false), 3000)
      return () => clearTimeout(t)
    }, [pendingDelete])

    const canCollapse = line.text.length > 320
    const displayHost = hostLabel || line.host
    const hasAudio = !!(line.audioFileUrl && line.audioFileUrl.trim())



    const handleTextChange = (newText: string) => {
      setEditText(newText)
    }

    const handleTextBlur = () => {
      setIsEditing(false)
      if (editText !== line.text) {
        onTextInput?.(editText)
      }
    }

    const handleTextFocus = () => {
      setIsEditing(true)
    }

    const { setLineHost, setLineEmotion } = usePodcastEditor()
    const { confirm } = useConfirm()
    const toggleHost = React.useCallback(async () => {
      if (isProcessing) return
      const lineHasAudio = !!(line.audioFileUrl && line.audioFileUrl.trim().length > 0)
      if (lineHasAudio) {
        // Provide suppressKey only here so this dialog can be permanently dismissed if user chooses
        const confirmed = await confirm({
          title: 'Change host for this line?',
          description: 'Changing the host will delete the generated audio for this line. You\'ll need to regenerate the audio, which will use additional credits.',
          confirmText: 'Change host',
          cancelText: 'Keep current host',
          variant: 'default',
          suppressKey: 'change-line-host'
        })
        if (!confirmed) return
      }
      const current = line.host === 'A' ? 'A' : 'B'
      const next = current === 'A' ? 'B' : 'A'
      setLineHost(line.id, next)
    }, [line.id, line.host, line.audioFileUrl, isProcessing, setLineHost, confirm])
    const nextHost = line.host === 'A' ? 'B' : 'A'
    const nextHostLabel = hostLabels?.[nextHost] || (line.host === 'A' ? 'Host B' : 'Host A')

    const deleteSuggestion = React.useMemo(() => (suggestions || []).find((s) => s.kind === 'delete'), [suggestions])
    const deleteBusy = applyingSuggestionId === deleteSuggestion?.id

    return (
      <ContextMenu>
        <div
          ref={containerRef}
          className={`relative group bg-white rounded-xl p-6 transition-all ${deleteSuggestion ? 'border-2 border-danger-400 ring-1 ring-danger-200' : 'border border-black/5'}`}
          tabIndex={0}
          aria-label={`Podcast line ${index !== undefined ? index + 1 : ''} by ${displayHost}`}
        >
          {!deleteSuggestion && (
            <>
              <ContextMenuContent>
                <ContextMenuLabel>Line actions</ContextMenuLabel>
                <ContextMenuItem onClick={(e) => { e.preventDefault(); onAddLineBelow?.() }}>
                  <Plus size={16} /> Add line below
                </ContextMenuItem>
                <ContextMenuItem
                  closeOnSelect={pendingDelete}
                  onClick={(e) => {
                    e.preventDefault()
                    if (isProcessing) return
                    if (!pendingDelete) {
                      setPendingDelete(true)
                      return
                    }
                    onDelete?.()
                    setPendingDelete(false)
                  }}
                  disabled={isProcessing}
                  aria-label={pendingDelete ? 'Confirm delete line' : 'Delete line'}
                  className={pendingDelete ? 'bg-danger-50 text-danger-700 hover:bg-danger-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger-400' : undefined}
                >
                  <Trash2 size={16} /> {pendingDelete ? 'Are you sure?' : 'Delete line'}
                </ContextMenuItem>
              </ContextMenuContent>
              <div className="absolute left-0 top-0">
                <ContextMenuTrigger asChild anchor="element">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Line options"
                    disabled={isProcessing}
                    className="h-7 w-7 p-0 rounded-lg hover:bg-black/5"
                  >
                    <MoreVertical size={16} />
                  </Button>
                </ContextMenuTrigger>
              </div>
            </>
          )}

          {deleteSuggestion ? (
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-danger-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-danger-700 ring-1 ring-danger-200">
                  Suggested removal
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={suggestionsDisabled || deleteBusy}
                  onClick={(e) => { e.preventDefault(); if (deleteSuggestion) onRejectSuggestion?.(deleteSuggestion) }}
                  className="cursor-pointer"
                  aria-label="Keep this line"
                  title="Keep this line"
                >
                  <Check className="h-4 w-4" /> Keep line
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="danger"
                  disabled={suggestionsDisabled || deleteBusy}
                  onClick={(e) => { e.preventDefault(); if (deleteSuggestion) onAcceptSuggestion?.(deleteSuggestion) }}
                  className="cursor-pointer"
                  aria-label="Delete this line"
                  title="Delete this line"
                >
                  {deleteBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Delete line
                </Button>
              </div>
            </div>
          ) : (
            <LineHeader
              index={index}
              displayHost={displayHost}
              hostColorClass={hostColorClass}
              hasAudio={hasAudio}
              isProcessing={isProcessing}
              status={line.status}
              onToggleHost={toggleHost}
              nextHostLabel={nextHostLabel}
              emotion={line.emotion}
              onEmotionChange={(e) => setLineEmotion(line.id, e)}
            />
          )}

          <div className="relative">
            <LineTextEditor
              lineId={line.id}
              text={editText}
              onTextChange={handleTextChange}
              onTextBlur={handleTextBlur}
              onTextFocus={handleTextFocus}
            />

            {!expanded && canCollapse && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white to-transparent" />
            )}
          </div>

          <LineFooter
            canCollapse={canCollapse}
            expanded={expanded}
            onToggleExpanded={() => setExpanded((v) => !v)}
          />

          {suggestions?.filter(s => s.kind !== 'delete').map((suggestion) => (
            <LineSuggestionCard
              key={suggestion.id}
              suggestion={suggestion}
              isActive={suggestion.id === activeSuggestionId}
              onAccept={(s) => onAcceptSuggestion?.(s)}
              onReject={(s) => onRejectSuggestion?.(s)}
              onFocus={(s) => onFocusSuggestion?.(s)}
              disabled={suggestionsDisabled}
              isBusy={applyingSuggestionId === suggestion.id}
            />
          ))}
        </div>
      </ContextMenu>
    )
  }
